import {Injectable} from '@angular/core';
import {
  Claim,
  SoftAndHardValidationMessages,
  ValidationMessage,
  ValidationMessageLevelCode
} from '../../../../models/claim';
import {ViewStateService} from '../../view-state/view-state.service';
import {isNullOrUndefined} from '../../../utility';

@Injectable({
  providedIn: 'root'
})
export class ClaimEditService {

  private hasHardEdits: boolean = false;
  private hasSoftEdits: boolean = false;
  private softAndHardEdits: SoftAndHardValidationMessages;

  constructor(
    private viewStateService: ViewStateService
  ) { }

  addHardEditMessage(message: ValidationMessage): void {
    if (!isNullOrUndefined(message)) {
      if (isNullOrUndefined(this.softAndHardEdits)) {
        this.softAndHardEdits = {
          hardEditMessages: [],
          unacknowledgedSoftEdits: []
        };
      }
      this.softAndHardEdits.hardEditMessages.push(message);
      this.hasHardEdits = true;
    }
  }

  clearHardEditsByLevel(levelCode: string): void {
    if (this.softAndHardEdits && this.softAndHardEdits.hardEditMessages) {
      this.softAndHardEdits.hardEditMessages = this.softAndHardEdits.hardEditMessages.filter(
        (message: ValidationMessage) => message.levelCode !== levelCode
      );
      this.hasHardEdits = this.softAndHardEdits.hardEditMessages.length > 0;
    }
  }

  clearHardEdits(): void {
    if (this.softAndHardEdits) {
      this.softAndHardEdits.hardEditMessages = [];
      this.hasHardEdits = false;
    }
  }

  clearRemoteEdits(): void {
    if (this.softAndHardEdits) {
      this.softAndHardEdits.hardEditMessages = this.softAndHardEdits.hardEditMessages.filter(
        (message: ValidationMessage) => message.levelCode === 'diagnosisCodes'
      );
      this.softAndHardEdits.unacknowledgedSoftEdits = [];
      this.hasHardEdits = this.softAndHardEdits.hardEditMessages.length > 0;
      this.hasSoftEdits = this.softAndHardEdits.unacknowledgedSoftEdits.length > 0;
    }
  }

  handleSoftAndHardEdits(activeClaim: Claim) {
    this.clearRemoteEdits();
    try {
      if (activeClaim) {
        const hardEditMessages: ValidationMessage[] = [];
        const unacknowledgedSoftEdits: ValidationMessage[] = [];

        // look for all Info and Error level validation messages in the patientEncounterValidationMessages from PE API response
        if (!isNullOrUndefined(activeClaim.patientEncounterValidationMessages) && activeClaim.patientEncounterValidationMessages.length > 0) {
          activeClaim.patientEncounterValidationMessages.forEach((patientEncounterValidationMessage: ValidationMessage) => {
            if (patientEncounterValidationMessage.levelCode === ValidationMessageLevelCode.ERROR) {
              hardEditMessages.push(patientEncounterValidationMessage);
              this.hasHardEdits = true;
            }
            if (patientEncounterValidationMessage.levelCode === ValidationMessageLevelCode.INFO &&
              !patientEncounterValidationMessage.acknowledged) {
              unacknowledgedSoftEdits.push(patientEncounterValidationMessage);
              this.hasSoftEdits = true;
            }
          });
        }

        // look for all Info and Error level validation messages at the service line level in the serviceLineValidationMessages from PE API response
        if (!isNullOrUndefined(activeClaim.serviceLines) && activeClaim.serviceLines.length > 0) {
          for (let i = 0; i < activeClaim.serviceLines.length; i++) {
            const serviceLine = activeClaim.serviceLines[i];
            if (!isNullOrUndefined(serviceLine.serviceLineValidationMessages) && serviceLine.serviceLineValidationMessages.length > 0) {
              serviceLine.serviceLineValidationMessages.forEach((serviceLineValidationMessage: ValidationMessage) => {
                if (serviceLineValidationMessage.levelCode === ValidationMessageLevelCode.ERROR) {
                  if (serviceLineValidationMessage.fieldIds && serviceLineValidationMessage.fieldIds.length > 0) {
                    serviceLineValidationMessage.fieldIds[0] = `serviceLines[${i}].${serviceLineValidationMessage.fieldIds[0]}`;
                  }
                  hardEditMessages.push(serviceLineValidationMessage);
                  this.hasHardEdits = true;
                }
                if (serviceLineValidationMessage.levelCode === ValidationMessageLevelCode.INFO &&
                  !serviceLineValidationMessage.acknowledged) {
                  if (serviceLineValidationMessage.fieldIds && serviceLineValidationMessage.fieldIds.length > 0) {
                    serviceLineValidationMessage.fieldIds[0] = `serviceLines[${i}].${serviceLineValidationMessage.fieldIds[0]}`;
                  }
                  unacknowledgedSoftEdits.push(serviceLineValidationMessage);
                  this.hasSoftEdits = true;
                }
              });
            }
          }
        }
        if (this.hasHardEdits || this.hasSoftEdits) {
          this.clearSoftAndHardEditHighlights();
          this.softAndHardEdits = {
            hardEditMessages: [
              ...(this.softAndHardEdits?.hardEditMessages || []),
              ...hardEditMessages
            ],
            unacknowledgedSoftEdits: [
              ...(this.softAndHardEdits?.unacknowledgedSoftEdits || []),
              ...unacknowledgedSoftEdits
            ]
          };
          this.viewStateService.setHasEdits(true);
        }
      }
    } catch (error) {
      console.error('Error occurred while handling edits:', error);
    }
  }

  hasHardEdit(): boolean {
    return this.hasHardEdits;
  }

  hasSoftEdit(): boolean {
    return this.hasSoftEdits;
  }

  getSoftAndHardEdits(): SoftAndHardValidationMessages {
    return this.softAndHardEdits;
  }

  clearSoftAndHardEditHighlights(): void {
    const invalidFormElements = document.querySelectorAll('' +
      '.hardEditTextBoxField, ' +
      '.hardEditRadioButtonField, ' +
      '.hardEditCheckBoxField, ' +
      '.hardEditDropDownField, ' +
      '.softEditTextBoxField, ' +
      '.softEditRadioButtonField, ' +
      '.softEditCheckBoxField, ' +
      '.softEditDropDownField');
    if (!isNullOrUndefined(invalidFormElements)) {
      for (let i = 0; i < invalidFormElements.length; i++) {
        invalidFormElements.item(i).classList.remove(
          'hardEditTextBoxField',
          'hardEditRadioButtonField',
          'hardEditCheckBoxField',
          'hardEditDropDownField',
          'softEditTextBoxField',
          'softEditRadioButtonField',
          'softEditCheckBoxField',
          'softEditDropDownField');
      }
    }
  }
}
